import StreamTabAnalytics from 'features/stream/analytics/StreamTabAnalytics';
import { escapeRegExp } from 'utils/general';
import {
  categories,
  LS_STREAM_TRENDING_TAB_DATE,
  reportCategories,
  streamTypes,
  STREAM_STATE,
  STREAM_TYPE,
} from './constants';

interface CategoriesProps {
  label: string;
  key: string;
  visible?: string[];
}

const getStreamCategories = (streamType: string) => {
  const streamCategories = categories[streamType];

  return streamCategories;
};

const getActiveCategory = (
  key: string,
  streamCategories: CategoriesProps[] = categories[STREAM_TYPE.GENERIC],
) => streamCategories.find((cat) => cat.key === key) || streamCategories[0];

const getActiveReportType = (key: string, reportTypes = reportCategories) =>
  reportTypes.find((report) => report.key === key) || reportCategories[0];

function decideFileType(file: string) {
  const filename = String(file.split('/').pop());
  const parsedFilename =
    filename.length > 33
      ? `${filename.substr(0, 20)}...${filename.substr(filename.length - 6, 6)}`
      : filename;
  const extension = parsedFilename.split('.').pop();

  switch (extension) {
    case 'pdf':
      return 'file-pdf';

    case 'xls':
    case 'xlsx':
      return 'file-xls';

    case 'doc':
    case 'docx':
      return 'file-doc';

    default:
      return 'file-ppt';
  }
}

const showFileName = (url: string) => {
  const cleanUrl = url.split('?')[0];

  const fullFilename = cleanUrl.split('/').pop() || '';

  const dotIndex = fullFilename.lastIndexOf('.');
  const name = dotIndex !== -1 ? fullFilename.slice(0, dotIndex) : fullFilename;
  const extension = dotIndex !== -1 ? fullFilename.slice(dotIndex) : '.pdf';

  if (name.length > 33) {
    return `${name.substr(0, 20)}...${name.substr(-6)}${extension}`;
  }

  return name + extension;
};

const getFilterParamFromLocalStorage = () => {
  if (typeof window !== 'undefined') {
    const filter: any = {};
    if (localStorage.getItem(STREAM_STATE.CATEGORY)) {
      filter.category = localStorage.getItem(STREAM_STATE.CATEGORY);
    }
    if (localStorage.getItem(STREAM_STATE.TYPE)) {
      filter.type =
        streamTypes.find(
          (item) => item.lsKey === localStorage.getItem(STREAM_STATE.TYPE),
        )?.key || 'latest';
    }
    if (localStorage.getItem(STREAM_STATE.TAB)) {
      filter.tabHeader = localStorage.getItem(STREAM_STATE.TAB);
    }
    if (localStorage.getItem(LS_STREAM_TRENDING_TAB_DATE)) {
      filter.date = localStorage.getItem(LS_STREAM_TRENDING_TAB_DATE);
    }

    return filter;
  }
  return null;
};

const handleAnalyticsTabView = (currentTab: string, filter: string) => {
  switch (currentTab) {
    case 'reports':
      // eslint-disable-next-line no-case-declarations
      const reportLabel =
        reportCategories.find((item) => item.key === filter)?.label || '';
      return StreamTabAnalytics.reports.View.onReportsViewed(reportLabel);
    case 'predictions':
      return StreamTabAnalytics.predictions.View.onPredictionsViewed();
    case 'insider':
      return StreamTabAnalytics.insiders.View.onInsidersViewed();
    case 'charts':
      return StreamTabAnalytics.charts.View.onChartsViewed();
    case 'ideas':
    default:
      // eslint-disable-next-line no-case-declarations
      const ideasType =
        streamTypes.find((item) => item.key === filter)?.label || '';
      return StreamTabAnalytics.ideas.View.onIdeasViewed(ideasType);
  }
};

// Stolen from https://stackoverflow.com/questions/8644428/how-to-highlight-text-using-javascript
function highlightWords(
  keyword: string,
  element: HTMLElement,
  tagName = 'mark',
) {
  // nosemgrep - detect-non-literal-regexp
  const keywordRegex = new RegExp(escapeRegExp(keyword), 'gi');
  const nodes = Array.from(element.childNodes);

  // Sort longer matches first to avoid highlighting keywords within keywords.
  nodes.forEach((node) => {
    const { textContent, parentNode, nodeType } = node;
    if (nodeType !== Node.TEXT_NODE) {
      // not a text node
      highlightWords(keyword, node as HTMLElement);
    } else if (keywordRegex.test(textContent)) {
      const fragment = document.createDocumentFragment();

      let lastIdx = 0;

      textContent.replace(keywordRegex, (match, idx) => {
        const part = document.createTextNode(textContent.slice(lastIdx, idx));
        const highlighted = document.createElement(tagName);
        highlighted.textContent = match;
        fragment.appendChild(part);
        fragment.appendChild(highlighted);
        lastIdx = idx + match.length;
        return match;
      });

      const end = document.createTextNode(textContent.slice(lastIdx));
      fragment.appendChild(end);
      parentNode.replaceChild(fragment, node);
    }
  });
}

export {
  getStreamCategories,
  getActiveCategory,
  getActiveReportType,
  decideFileType,
  showFileName,
  getFilterParamFromLocalStorage,
  handleAnalyticsTabView,
  highlightWords,
};
